import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  BeakerIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { customRulesApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import { PageHeader, StatusBadge } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

const SEVERITY_BADGE: Record<string, BadgeVariant> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  CRITICAL: 'danger',
};

const TRIGGER_LABELS: Record<string, string> = {
  KEYWORD_PRESENT: 'If text contains',
  KEYWORD_MISSING: 'If text is missing',
  PATTERN_MATCH: 'If text matches pattern',
};

const DEFAULT_FORM = {
  name: '',
  description: '',
  triggerType: 'KEYWORD_PRESENT',
  triggerValue: '',
  // Optional second condition ('' = none) for conditional "if X then require Y" rules.
  secondTriggerType: '',
  secondTriggerValue: '',
  requiredAction: '',
  severity: 'MEDIUM',
  regulation: '',
};

export default function CustomRulesPage() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['customRules', teamId],
    queryFn: () => customRulesApi.list(teamId!),
    enabled: !!teamId,
  });
  const rules = data?.data?.rules || [];

  const createMutation = useMutation({
    mutationFn: (d: any) => customRulesApi.create(teamId!, d),
    onSuccess: () => {
      toast.success('Custom rule created');
      queryClient.invalidateQueries({ queryKey: ['customRules'] });
      closeModal();
    },
    onError: () => toast.error('Failed to create rule'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }: { id: string; data: any }) => customRulesApi.update(teamId!, id, d),
    onSuccess: () => {
      toast.success('Rule updated');
      queryClient.invalidateQueries({ queryKey: ['customRules'] });
      closeModal();
    },
    onError: () => toast.error('Failed to update rule'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customRulesApi.delete(teamId!, id),
    onSuccess: () => {
      toast.success('Rule deleted');
      queryClient.invalidateQueries({ queryKey: ['customRules'] });
    },
    onError: () => toast.error('Failed to delete rule'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      customRulesApi.update(teamId!, id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customRules'] }),
  });

  const testMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      customRulesApi.test(teamId!, id, text),
    onSuccess: (res) => setTestResult(res.data),
  });

  function openCreate() {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setShowModal(true);
  }

  function openEdit(rule: any) {
    setEditing(rule);
    setForm({
      name: rule.name,
      description: rule.description || '',
      triggerType: rule.triggerType,
      triggerValue: rule.triggerValue,
      secondTriggerType: rule.secondTriggerType || '',
      secondTriggerValue: rule.secondTriggerValue || '',
      requiredAction: rule.requiredAction,
      severity: rule.severity,
      regulation: rule.regulation || '',
    });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditing(null);
    setForm(DEFAULT_FORM);
  }

  function handleSubmit() {
    // Omit the second condition unless a type was chosen (the API rejects an empty
    // enum and requires type+value together).
    const payload = {
      ...form,
      secondTriggerType: form.secondTriggerType || undefined,
      secondTriggerValue: form.secondTriggerType ? form.secondTriggerValue : undefined,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function handleTest(ruleId: string) {
    setTestingId(ruleId);
    setTestResult(null);
    setTestText('');
  }

  return (
    <FeatureGate feature="basic_compliance">
      <div className="space-y-6">
        <PageHeader
          title="Custom Compliance Rules"
          description="Define institution-specific rules that run alongside standard regulatory checks. &quot;If we say X, we must say Y.&quot;"
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <button onClick={openCreate} className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-500 transition-colors">
                <PlusIcon className="h-4 w-4" />
                Add Rule
              </button>
            </PermissionGate>
          }
        />

        {/* Rules List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-teal-600" />
          </div>
        ) : rules.length === 0 ? (
          <div className="rounded-lg border bg-white p-12 text-center">
            <AdjustmentsHorizontalIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-sm font-medium text-gray-900">No custom rules yet</p>
            <p className="mt-1 text-sm text-gray-500">
              Custom rules let you enforce institution-specific SOPs alongside standard regulatory checks.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule: any) => (
              <div key={rule.id} className="rounded-lg border bg-white p-5 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-navy-900">{rule.name}</h3>
                      <StatusBadge variant={SEVERITY_BADGE[rule.severity] || 'neutral'}>
                        {rule.severity}
                      </StatusBadge>
                      {rule.regulation && (
                        <span className="text-xs text-gray-400">{rule.regulation}</span>
                      )}
                      <span className={clsx(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        rule.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                      )}>
                        {rule.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {rule.description && (
                      <p className="mt-1 text-sm text-gray-500">{rule.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400">
                      <span>{TRIGGER_LABELS[rule.triggerType] || rule.triggerType}:</span>
                      <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-600">
                        {rule.triggerValue.length > 60 ? rule.triggerValue.slice(0, 60) + '...' : rule.triggerValue}
                      </code>
                      {rule.secondTriggerType && (
                        <>
                          <span className="font-medium text-teal-600">AND</span>
                          <span>{TRIGGER_LABELS[rule.secondTriggerType] || rule.secondTriggerType}:</span>
                          <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-600">
                            {(rule.secondTriggerValue || '').length > 60 ? rule.secondTriggerValue.slice(0, 60) + '...' : rule.secondTriggerValue}
                          </code>
                        </>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                      Required action: {rule.requiredAction.length > 80 ? rule.requiredAction.slice(0, 80) + '...' : rule.requiredAction}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => handleTest(rule.id)} className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-teal-600" title="Test rule">
                      <BeakerIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => openEdit(rule)} className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-teal-600" title="Edit">
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => toggleMutation.mutate({ id: rule.id, isActive: !rule.isActive })}
                      className={clsx('rounded px-2 py-1 text-xs font-medium transition-colors', rule.isActive ? 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600' : 'bg-green-50 text-green-600 hover:bg-green-100')}
                    >
                      {rule.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => { if (confirm('Delete this rule?')) deleteMutation.mutate(rule.id); }} className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Test Panel */}
                {testingId === rule.id && (
                  <div className="mt-4 rounded-lg bg-gray-50 p-4 border">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Paste sample text to test this rule:</label>
                    <textarea
                      rows={3}
                      value={testText}
                      onChange={(e) => setTestText(e.target.value)}
                      placeholder="e.g., Get a cash advance up to $500 today!"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                    />
                    <div className="mt-2 flex items-center gap-3">
                      <button
                        onClick={() => testMutation.mutate({ id: rule.id, text: testText })}
                        disabled={!testText.trim() || testMutation.isPending}
                        className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                      >
                        {testMutation.isPending ? 'Testing...' : 'Run Test'}
                      </button>
                      <button onClick={() => { setTestingId(null); setTestResult(null); }} className="text-xs text-gray-500 hover:text-gray-700">
                        Close
                      </button>
                      {testResult && (
                        <span className={clsx('text-xs font-medium', testResult.triggered ? 'text-red-600' : 'text-green-600')}>
                          {testResult.triggered ? `Rule triggered — ${testResult.violations?.length || 0} violation(s)` : 'No match — rule did not trigger'}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Transition appear show={showModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeModal}>
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      {editing ? 'Edit Custom Rule' : 'Add Custom Rule'}
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      {editing ? 'Update this custom compliance rule.' : 'Define an institution-specific compliance rule.'}
                    </p>

                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Rule Name <span className="text-red-500">*</span></label>
                        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder='e.g., "Cash advance fee disclosure"' className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Trigger Type</label>
                          <select value={form.triggerType} onChange={(e) => setForm({ ...form, triggerType: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500">
                            <option value="KEYWORD_PRESENT">If text contains keyword</option>
                            <option value="KEYWORD_MISSING">If text is missing keyword</option>
                            <option value="PATTERN_MATCH">If text matches regex pattern</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Severity</label>
                          <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500">
                            <option value="LOW">Low</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="HIGH">High</option>
                            <option value="CRITICAL">Critical</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Trigger Value <span className="text-red-500">*</span>
                          <span className="ml-1 text-xs text-gray-400">
                            ({form.triggerType === 'PATTERN_MATCH' ? 'regex pattern' : 'keyword or comma-separated keywords'})
                          </span>
                        </label>
                        <input type="text" value={form.triggerValue} onChange={(e) => setForm({ ...form, triggerValue: e.target.value })} placeholder={form.triggerType === 'PATTERN_MATCH' ? '(?i)cash\\s+advance' : 'cash advance, payday'} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-teal-500 focus:ring-teal-500" />
                      </div>

                      {/* Optional second condition — "if X then require Y" conditional rules */}
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Second condition <span className="text-xs font-normal text-gray-400">(optional — only flags when BOTH match)</span>
                        </label>
                        <p className="mt-0.5 text-xs text-gray-500">
                          For conditional rules like “if the ad says <span className="font-mono">up to $500</span>, it must also say <span className="font-mono">work your way up</span>” — set the trigger above to the claim, then require the disclosure here.
                        </p>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          <select
                            value={form.secondTriggerType}
                            onChange={(e) => setForm({ ...form, secondTriggerType: e.target.value })}
                            className="col-span-1 rounded-lg border border-gray-300 px-2 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          >
                            <option value="">— none —</option>
                            <option value="KEYWORD_MISSING">…and is missing</option>
                            <option value="KEYWORD_PRESENT">…and also contains</option>
                            <option value="PATTERN_MATCH">…and matches pattern</option>
                          </select>
                          <input
                            type="text"
                            value={form.secondTriggerValue}
                            disabled={!form.secondTriggerType}
                            onChange={(e) => setForm({ ...form, secondTriggerValue: e.target.value })}
                            placeholder={form.secondTriggerType === 'PATTERN_MATCH' ? '(?i)work your way up' : 'work your way up'}
                            className="col-span-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-teal-500 focus:ring-teal-500 disabled:bg-gray-100 disabled:text-gray-400"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Required Action <span className="text-red-500">*</span></label>
                        <textarea rows={2} value={form.requiredAction} onChange={(e) => setForm({ ...form, requiredAction: e.target.value })} placeholder='e.g., "Must include: fees may apply, see terms and conditions"' className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Regulation (optional)</label>
                        <input type="text" value={form.regulation} onChange={(e) => setForm({ ...form, regulation: e.target.value })} placeholder="e.g., Internal SOP, Reg E, Reg Z" className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500" />
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button onClick={closeModal} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                      <button
                        onClick={handleSubmit}
                        disabled={!form.name.trim() || !form.triggerValue.trim() || !form.requiredAction.trim() || createMutation.isPending || updateMutation.isPending}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                      >
                        {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : editing ? 'Update Rule' : 'Create Rule'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
